import { addDefaultIfEmpty, insertAfterIndex, removeAtIndex, sum, updateWhere } from '../../helpers/array'
import { uniqueId } from '../../helpers/uniqueId'

type Equipment = {
  id: string
  name: string
}

export type EquipmentType = 'OWNED' | 'LEASED' | 'MISC'
export type RecoveryStrategy = 'OVERHEAD' | 'BILLABLE'

type StandardEquipment = Equipment & {
  quantity: number
  recoveryStrategy: RecoveryStrategy
  billableHoursPerYear: number
}

export type OwnedEquipment = StandardEquipment & {
  purchasePrice: number
  yearsUsed: number
  sellPrice: number
}

export type LeasedEquipment = StandardEquipment & {
  monthlyPayment: number
}

export type MiscEquipment = Equipment & {
  totalAnnualCost: number
}

export type EquipmentList = {
  owned: OwnedEquipment[],
  leased: LeasedEquipment[],
  misc: MiscEquipment[]
}


export const emptyMiscEquipment = (): MiscEquipment => ({
  id: uniqueId(),
  name: '',
  totalAnnualCost: 0
})

export const emptyOwnedEquipment = (): OwnedEquipment => ({
  id: uniqueId(),
  name: '',
  recoveryStrategy: 'OVERHEAD',
  billableHoursPerYear: 0,
  purchasePrice: 0,
  yearsUsed: 0,
  sellPrice: 0,
  quantity: 1
})

export const emptyLeasedEquipment = (): LeasedEquipment => ({
  id: uniqueId(),
  name: '',
  recoveryStrategy: 'OVERHEAD',
  billableHoursPerYear: 0,
  monthlyPayment: 0,
  quantity: 1
})

export const createDefaultEquipment = (): EquipmentList => ({
  owned: [emptyOwnedEquipment()],
  leased: [emptyLeasedEquipment()],
  misc: [emptyMiscEquipment()]
})

export const addEquipment = (equipmentList: EquipmentList, type: EquipmentType, index: number): EquipmentList => {
  if (type === 'MISC') {
    return {
      ...equipmentList,
      misc: insertAfterIndex(equipmentList.misc, index, emptyMiscEquipment())
    }
  }
  if (type === 'LEASED') {
    return {
      ...equipmentList,
      leased: insertAfterIndex(equipmentList.leased, index, emptyLeasedEquipment())
    }
  }
  return {
    ...equipmentList,
    owned: insertAfterIndex(equipmentList.owned, index, emptyOwnedEquipment())
  }
}

export const removeEquipment = (equipmentList: EquipmentList, type: EquipmentType, index: number): EquipmentList => {
  if (type === 'LEASED') {
    return {
      ...equipmentList,
      leased: addDefaultIfEmpty(removeAtIndex(equipmentList.leased, index), emptyLeasedEquipment())
    }  
  }

  if (type === 'MISC') {
    return {
      ...equipmentList,
      misc: addDefaultIfEmpty(removeAtIndex(equipmentList.misc, index), emptyMiscEquipment())
    }  
  }
  
  return {
    ...equipmentList,
    owned: addDefaultIfEmpty(removeAtIndex(equipmentList.owned, index), emptyOwnedEquipment())
  }
}

export const updateName = (equipmentList: EquipmentList, id: string, name: string): EquipmentList => {
  return {
    ...equipmentList,
    owned: updateWhere(equipmentList.owned, x => x.id === id, x => ({...x, name})),
    leased: updateWhere(equipmentList.leased, x => x.id === id, x => ({...x, name})),
    misc: updateWhere(equipmentList.misc, x => x.id === id, x => ({...x, name}))
  }
}

export const updateQuantity = (equipmentList: EquipmentList, id: string, quantity: number): EquipmentList => {
  return {
    ...equipmentList,
    owned: updateWhere(equipmentList.owned, x => x.id === id, x => ({...x, quantity})),
    leased: updateWhere(equipmentList.leased, x => x.id === id, x => ({...x, quantity})),
  }
}

export const updateRecoveryStrategy = (equipmentList: EquipmentList, id: string, recoveryStrategy: RecoveryStrategy): EquipmentList => {
  return {
    ...equipmentList,
    owned: updateWhere(equipmentList.owned, x => x.id === id, x => ({...x, recoveryStrategy})),
    leased: updateWhere(equipmentList.leased, x => x.id === id, x => ({...x, recoveryStrategy})),
  }
}

export const updateBillableHoursPerYear = (equipmentList: EquipmentList, id: string, billableHoursPerYear: number): EquipmentList => {
  return {
    ...equipmentList,
    owned: updateWhere(equipmentList.owned, x => x.id === id, x => ({...x, billableHoursPerYear})),
    leased: updateWhere(equipmentList.leased, x => x.id === id, x => ({...x, billableHoursPerYear})),
  }
}

export const updatePurchasePrice = (equipmentList: EquipmentList, id: string, purchasePrice: number): EquipmentList => {
  return {
    ...equipmentList,
    owned: updateWhere(equipmentList.owned, x => x.id === id, x => ({...x, purchasePrice})),
  }
}

export const updateYearsUsed = (equipmentList: EquipmentList, id: string, yearsUsed: number): EquipmentList => {
  return {
    ...equipmentList,
    owned: updateWhere(equipmentList.owned, x => x.id === id, x => ({...x, yearsUsed})),
  }
}

export const updateSellPrice = (equipmentList: EquipmentList, id: string, sellPrice: number): EquipmentList => {
  return {
    ...equipmentList,
    owned: updateWhere(equipmentList.owned, x => x.id === id, x => ({...x, sellPrice})),
  }
}

export const updateMonthlyPayment = (equipmentList: EquipmentList, id: string, monthlyPayment: number): EquipmentList => {
  return {
    ...equipmentList,
    leased: updateWhere(equipmentList.leased, x => x.id === id, x => ({...x, monthlyPayment})),
  }
}

export const updateAnnualCost = (equipmentList: EquipmentList, id: string, totalAnnualCost: number): EquipmentList => {
  return {
    ...equipmentList,
    misc: updateWhere(equipmentList.misc, x => x.id === id, x => ({...x, totalAnnualCost})),
  }
}

export type StandardItemStatistics = {
  annualCostToOwn: number,
  totalAnnualCost: number,
  hourlyCostToRun: number
}

export type OwnedEquipmentWithStats = OwnedEquipment & StandardItemStatistics;
export type LeasedEquipmentWithStats = LeasedEquipment & StandardItemStatistics;

export type EquipmentListWithStats = {
  owned: OwnedEquipmentWithStats[],
  leased: LeasedEquipmentWithStats[],
  misc: MiscEquipment[],
}

const calculateOwnedItemStatistics = (equipment: OwnedEquipment): OwnedEquipmentWithStats => {
  const annualCostToOwn = equipment.yearsUsed === 0
    ? 0
    : (equipment.purchasePrice - equipment.sellPrice) / equipment.yearsUsed
  const hourlyCostToRun = equipment.billableHoursPerYear === 0 
    ? 0 
    : annualCostToOwn / equipment.billableHoursPerYear
  return {
    ...equipment,
    annualCostToOwn,
    totalAnnualCost: annualCostToOwn * equipment.quantity,
    hourlyCostToRun
  }
}

const calculateLeasedItemStatistics = (equipment: LeasedEquipment): LeasedEquipmentWithStats => {
  const annualCostToOwn = equipment.monthlyPayment * 12
  const hourlyCostToRun = equipment.billableHoursPerYear === 0 
    ? 0 
    : annualCostToOwn / equipment.billableHoursPerYear
  return {
    ...equipment,
    annualCostToOwn,
    totalAnnualCost: annualCostToOwn * equipment.quantity,
    hourlyCostToRun
  }
}

export const calculateItemStatistics = (equipmentList: EquipmentList): EquipmentListWithStats => {
  return {
    owned: equipmentList.owned.map(calculateOwnedItemStatistics),
    leased: equipmentList.leased.map(calculateLeasedItemStatistics),
    misc: equipmentList.misc
  }
}

export type EquipmentStatistics = {
  overheadAnnualCost: number
  billableAnnualCost: number
  totalAnnualCost: number
}

export const calculateOverallStatistics = (equipmentList: EquipmentListWithStats): EquipmentStatistics  => {
  const overheadAnnualCost = sum([
    ...equipmentList.owned.filter(x => x.recoveryStrategy === 'OVERHEAD'),
    ...equipmentList.leased.filter(x => x.recoveryStrategy === 'OVERHEAD'),
    ...equipmentList.misc
  ], x => x.totalAnnualCost)
  const billableAnnualCost = sum([
    ...equipmentList.owned.filter(x => x.recoveryStrategy === 'BILLABLE'),
    ...equipmentList.leased.filter(x => x.recoveryStrategy === 'BILLABLE')
  ], x => x.totalAnnualCost)
  return {
    overheadAnnualCost,
    billableAnnualCost,
    totalAnnualCost: overheadAnnualCost + billableAnnualCost
  }
}

export const enforceInvariants = (equipmentList: EquipmentList | undefined): EquipmentList => {
  if (!equipmentList) {
    return createDefaultEquipment()
  }
  return {
    owned: equipmentList.owned.length === 0 ? [emptyOwnedEquipment()] : equipmentList.owned,
    leased: equipmentList.leased.length === 0 ? [emptyLeasedEquipment()] : equipmentList.leased,
    misc: equipmentList.misc.length === 0 ? [emptyMiscEquipment()] : equipmentList.misc,
  }
}